import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import type React from "react";
import { useMemo } from "react";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useGetRecentInvoices, useGetUpcomingInvoice } from "../../api/hooks";
import { createInvoiceColumns } from "./columns";
import { EmptyState } from "./empty-state";
import { UpcomingInvoiceSection } from "./upcoming-invoice-section";

type UnifiedInvoicesCardProps = {
  formatDate: (date?: string | null) => string;
  formatCurrency: (amount: string, currency?: string) => string;
};

export const UnifiedInvoicesCard: React.FC<UnifiedInvoicesCardProps> = ({
  formatDate,
  formatCurrency,
}) => {
  const { organizationId } = useOrgProjectRouteContext();
  const { data: recentInvoices } = useGetRecentInvoices(organizationId);
  const { data: upcomingInvoice } = useGetUpcomingInvoice(organizationId);

  const columns = useMemo(
    () => createInvoiceColumns(formatDate, formatCurrency),
    [formatDate, formatCurrency]
  );

  const hasUpcoming = Boolean(upcomingInvoice);
  const hasRecent = recentInvoices && recentInvoices.length > 0;

  if (!(hasUpcoming || hasRecent)) {
    return (
      <Card>
        <CardContent>
          <EmptyState />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {hasUpcoming && (
            <UpcomingInvoiceSection
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              invoice={upcomingInvoice}
            />
          )}
          {hasRecent && (
            <div
              className={hasUpcoming ? "border-border/50 border-t pt-6" : ""}
            >
              <div className="mb-8">
                <h3 className="font-bold text-base">Recent Invoices</h3>
              </div>
              <DataTable
                className="space-y-4"
                columns={columns}
                data={recentInvoices}
                defaultPageSize={5}
              >
                <DataTableContent stickyColumnBg="card" />
                <DataTablePagination />
              </DataTable>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
